import { AppData, Registry } from '@lightningjs/sdk';
import mParticle from '@mparticle/web-sdk';
import { Batch } from '@mparticle/event-models';
//@ts-ignore
import brazeKit from '@mparticle/web-braze-kit';
import '@mparticle/web-google-analytics-4-client-kit';
import {
  Episode,
  LiveEvent,
  LiveEventStream,
  Media,
  Video,
} from 'types/api/media';
import {
  getSeriesName,
  isContentHub,
  isEpisode,
  isLiveEvent,
  isPromoChannel,
  isVideo,
} from 'support/contentUtils';
import {
  BrowsePageEngagement,
  FormSkipPayload,
  GetStartedPayload,
  HeaderNavigationPayload,
  HeroEngagementPayload,
  ItemCustomAttrs,
  LoginEventPayload,
  LogoutEventPayload,
  MParticleEvents,
  PageViewPayload,
  PromotionCustomAttrs,
  RowEngagementPayload,
  ScrollTrackingPayload,
  SearchResultClicked,
  ShowPageEngagement,
  SignUpPasswordPayload,
  SignUpMagicLinkPayload,
  ChannelEngagementPayload,
  MyStuffAddRemovePayload,
} from 'types/analytics/mParticle';
import MParticleVideo from 'services/analytics/MParticleVideo';
import {
  ErrorCode,
  reportError,
} from 'services/analytics/reportingServicePage';
import { ContentHub, Promo } from 'services/cwData';
import {
  createMParticleProduct,
  createMParticlePromotion,
  getSourceSite,
} from 'support/mParticleUtils';
import { metadata as appMetadata } from 'aliases';
import { AbstractDeviceIntegration } from 'config/platforms/AbstractDeviceIntegration';
import { PrivacyConsent } from 'services/PrivacyService';
import { getPageId, PageId } from 'types/pageId';
import { translate } from 'support/translate';
import { Platform } from 'models/platforms/platform';
import { DeviceInfo } from 'models/platforms/deviceInfo';
import { Account } from 'types/loginRadius';
import { LoginFlow } from 'components/pages/login/layout/AbstractLoginModal';

const USER_IDENTITY_TIMEOUT_MS = 15000;

export type ViewItemProps = {
  crossIndex: number | undefined;
  imageUrl: string | undefined;
  mainIndex: number | undefined;
  // @ts-ignore
  data: Media | ContentHub | undefined;
  rowTitle: string | undefined;
  isTivoTileSource: boolean;
};
export type SelectItemProps = ViewItemProps;

export type SelectPromoProps = {
  promo: Promo | undefined;
  imageFileTitle: string | undefined;
  index: number | undefined;
};

export type LoginType = 'Magic Link' | 'Password' | 'Other';

export type LoginProps = {
  loginType: LoginType;
  uid: string;
  primaryEmail: string;
  pageId: PageId;
};

export type LogoutProps = {
  loginType: LoginType;
  uid: string;
  pageId: PageId;
};

export type MultipleSeriesNameTypes = 'Multiple' | 'Select All';

export type MyStuffAddRemoveProps = {
  actionType: 'Add' | 'Remove';
  type: MultipleSeriesNameTypes | string;
  pageId: PageId;
  rowName: 'Continue Watching' | 'Watchlist' | 'Channels';
};

type ConstructorArgs = {
  deviceIntegration: AbstractDeviceIntegration;
  deviceInfo: DeviceInfo;
};

export default class MParticleAnalytics {
  static readonly serviceName = 'MParticleAnalytics';

  private readonly deviceIntegration: AbstractDeviceIntegration;

  private consentStateOn: PrivacyConsent | null = null;
  private waitForUserIdentity: Promise<void> | null = null;
  private userIdentityStored: (() => void) | null = null;
  private deviceInfo: DeviceInfo | null = null;
  video: MParticleVideo;
  braze: any;

  static async AsyncConstruct({
    deviceIntegration,
    deviceInfo,
  }: ConstructorArgs) {
    if (AppData === undefined) {
      throw new Error('AppData not set');
    }

    const instance = new MParticleAnalytics({
      deviceIntegration,
      deviceInfo,
    });

    await instance.waitForUserIdentity;

    if (!mParticle.isInitialized()) {
      reportError({
        error: new Error('mParticle SDK not initialized'),
        userFacing: false,
        code: ErrorCode.c48,
      });
    }

    return instance;
  }

  get mpid() {
    return AppData!.mParticle.mpid;
  }

  private constructor({ deviceIntegration, deviceInfo }: ConstructorArgs) {
    if (AppData === undefined) {
      throw new Error('AppData not set');
    }

    this.deviceIntegration = deviceIntegration;
    this.deviceInfo = deviceInfo;

    this.setUserIdentityPromise();

    const { isProduction, debug } = AppData;
    const { dataPlan, key } = AppData.mParticle;

    const config: mParticle.MPConfiguration = {
      appName: appMetadata.title,
      appVersion: appMetadata.version,
      isDevelopmentMode: !isProduction,
      logLevel: debug ? 'verbose' : 'none',
      dataPlan: { planId: dataPlan.id },
      identityCallback: this.storeUserIdentity.bind(this),
      onCreateBatch: this.onCreateBatch,
      identifyRequest: {
        userIdentities: {
          // Reserved identity for SmartTV
          other2: deviceInfo.adId,
        },
      },
    };

    brazeKit.register(config);
    mParticle.init(key, config);

    this.video = new MParticleVideo();
    this.braze = window.braze;
  }

  private setUserIdentityPromise() {
    this.waitForUserIdentity = new Promise(resolve => {
      this.userIdentityStored = resolve;

      // Identity request could potentially stall app launch forever
      // if SDK errors. Adding fallback timeout
      Registry.setTimeout(resolve, USER_IDENTITY_TIMEOUT_MS);
    });
  }

  private storeUserIdentity(data: mParticle.IdentityResult) {
    const user = data.getUser();
    if (user) AppData!.mParticle.mpid = user.getMPID();

    AppData!.mParticle.sessionId = mParticle.sessionManager.getSession();

    // Update privacy status if consent state was set before user identity
    if (this.consentStateOn !== null)
      this.updatePrivacyStatus(this.consentStateOn);

    this.userIdentityStored?.();
  }

  private onCreateBatch = (batch: Batch) => {
    const info = this.getBrandInfo();
    batch.device_info = {
      brand: info.brand,
      product: info.product,
    };

    return batch;
  };

  private getBrandInfo(): { brand: string; product?: string } {
    const platform = this.deviceIntegration.getPlatform();

    switch (platform) {
      case Platform.TIZEN:
        return { brand: 'Samsung TV', product: 'Samsung TV' };
      case Platform.LG:
        return { brand: 'LGTV', product: 'LGTV' };
      case Platform.VIZIO:
        return { brand: 'Vizio', product: 'Vizio' };
      case Platform.COMCAST:
        return { brand: 'Comcast', product: this.deviceInfo?.model };
      default:
        return { brand: 'Unknown', product: 'Unknown' };
    }
  }

  /**
   * This gets the login state of mParticle
   * This is independent of our login state (mParticle stores its own login state)
   * We should not use this state to determine if we are logged in, it is possible for this state
   * to desync from our internal login state
   * */
  isMParticleLoggedIn(): boolean {
    const currentUser = mParticle.Identity.getCurrentUser();
    return currentUser?.isLoggedIn() ?? false;
  }

  async login(account: Account) {
    const identityRequest = {
      userIdentities: {
        email: account.primaryEmail,
        customerid: account.uid,
      },
    };

    const isInitialized = mParticle.isInitialized();
    if (isInitialized) this.setUserIdentityPromise();

    mParticle.Identity.login(
      identityRequest,
      this.storeUserIdentity.bind(this),
    );

    if (isInitialized) await this.waitForUserIdentity;
  }

  async logout() {
    this.setUserIdentityPromise();

    mParticle.Identity.logout({}, this.storeUserIdentity.bind(this));

    await this.waitForUserIdentity;
  }

  reportPageView(pageId: PageId | string, pageName?: string) {
    const payload: PageViewPayload = {
      Source_Page: getPageId(pageId),
      login_status: AppData?.loginRadiusService?.isLoggedIn() ?? false,
      Source_Site: getSourceSite(),
      Page_Title: pageName || getPageId(pageId),
    };

    this.logEvent(
      MParticleEvents.PAGE_VIEW,
      mParticle.EventType.Other,
      payload,
    );
  }

  // Must be called before navigation
  reportNavBarNavigation(title: string) {
    const payload: HeaderNavigationPayload = {
      category: title,
    };

    this.logEvent(
      MParticleEvents.HEADER_NAVIGATION,
      mParticle.EventType.Other,
      payload,
    );
  }

  reportShowPageEngagement(
    mediaItem: Video | LiveEventStream,
    season: string,
    episodeIndex: number,
    seasonIndex: number,
    pageId: PageId,
  ) {
    const seriesName = getSeriesName(mediaItem);
    const title = mediaItem.title;

    const payload: ShowPageEngagement = {
      Series_Name: seriesName ?? '',
      Season_Name: season
        ? translate('seriesDetail.seasonTabTitle', season)
        : '', // use title value (Season 1, Season 2, etc)
      Episode_Name: title ?? '',
      row_position: `${seasonIndex + 1}`,
      row_name: season ?? '',
      creative_position: `${episodeIndex + 1}`,
      Source_Page: getPageId(pageId),
    };

    this.logEvent(
      MParticleEvents.SHOW_LIST_ITEM_CLICKED,
      mParticle.EventType.Other,
      payload,
    );
  }

  reportBrowsePageEngagement(
    seriesName: string,
    rowName: string,
    rowPosition: number,
    creativePosition: number,
  ) {
    const payload: BrowsePageEngagement = {
      row_name: rowName,
      row_position: `${rowPosition + 1}`,
      creative_position: `${creativePosition + 1}`,
      Series_Name: seriesName,
      Source_Site: getSourceSite(),
      Source_Page: getPageId(PageId.SEARCH),
    };

    this.logEvent(
      MParticleEvents.BROWSE_LIST_ITEM_CLICKED,
      mParticle.EventType.Other,
      payload,
    );
  }

  reportSearchResultClicked(
    mediaItem: Media,
    rowName: string,
    rowPosition: number,
    creativePosition: number,
    searchTerm: string,
  ) {
    const payload: SearchResultClicked = {
      row_name: rowName,
      row_position: `${rowPosition + 1}`,
      creative_position: `${creativePosition + 1}`,
      Series_Name: getSeriesName(mediaItem) ?? '',
      Search_Term: searchTerm,
      Source_Site: getSourceSite(),
      Source_Page: getPageId(PageId.SEARCH),
    };

    this.logEvent(
      MParticleEvents.SEARCH_RESULT_CLICKED,
      mParticle.EventType.Other,
      payload,
    );
  }

  reportScrollTracking(quartile: number, pageId: PageId) {
    const payload: ScrollTrackingPayload = {
      scroll_depth: quartile.toString() + '%',
      Source_Page: getPageId(pageId),
    };

    this.logEvent(
      MParticleEvents.SCROLL_TRACKING,
      mParticle.EventType.Other,
      payload,
    );
  }

  reportViewPromotion(props: { promo: Promo; index: number }, pageId: PageId) {
    const { promo, index } = props;
    const episodeName = this.getEpisodeName(promo);
    const seriesName = getSeriesName(promo);

    const promotion = createMParticlePromotion({
      creative: episodeName,
      id: promo.id,
      name: seriesName,
      position: index + 1,
    });

    const customAttributes: PromotionCustomAttrs = {
      Series_Name: seriesName,
      Source_Page: getPageId(pageId),
      creative_position: `${index + 1}`,
    };
    if (episodeName) {
      customAttributes['Episode_Name'] = episodeName;
    }

    this.logPromotionEvent(
      mParticle.PromotionType.PromotionView,
      promotion,
      customAttributes,
    );
  }

  reportPromoSelected(props: SelectPromoProps, pageId: PageId) {
    this.reportHeroEngagement(props, pageId);
  }

  private reportHeroEngagement(props: SelectPromoProps, pageId: PageId) {
    const { imageFileTitle, index, promo } = props;
    if (
      imageFileTitle === undefined ||
      index === undefined ||
      promo === undefined
    )
      return;

    const episodeName = this.getEpisodeName(promo);
    const seriesName = getSeriesName(promo);

    const payload: HeroEngagementPayload = {
      tivo_query: props.promo?.id ?? '',
      category: props.promo?.type ?? '',
      Source_Site: getSourceSite(),
      Series_Name: seriesName,
      creative_position: `${index + 1}`,
      Source_Page: getPageId(pageId),
      Episode_Name: episodeName ?? '',
    };

    this.logEvent(
      MParticleEvents.HERO_ENGAGEMENT,
      mParticle.EventType.Other,
      payload,
    );
  }

  reportItemSelected(props: SelectItemProps, pageId: PageId) {
    this.reportRowEngagement(props, pageId);
  }

  private reportRowEngagement(props: SelectItemProps, pageId: PageId) {
    const { data, mainIndex, crossIndex, rowTitle, isTivoTileSource } = props;

    if (
      data === undefined ||
      mainIndex === undefined ||
      crossIndex === undefined ||
      rowTitle === undefined
    ) {
      return;
    }

    const episodeName = this.getEpisodeName(data);
    const seriesName = getSeriesName(data);

    const payload: RowEngagementPayload = {
      tivo_query: props.data?.id ?? '',
      category: props.data?.type ?? '',
      Source_Page: getPageId(pageId),
      Source_Site: getSourceSite(),
      row_name: rowTitle,
      Series_Name: seriesName,
      row_position: `${mainIndex + 1}`,
      creative_position: `${crossIndex + 1}`,
      Tile_Source: isTivoTileSource ? 'Tivo Screens' : 'CMS',
      Episode_Name: episodeName ?? '',
    };

    this.logEvent(
      MParticleEvents.ROW_ENGAGEMENT,
      mParticle.EventType.Other,
      payload,
    );
  }

  reportGetStarted(pageId: PageId) {
    const payload: GetStartedPayload = {
      Source_Site: getSourceSite(),
      Source_Page: getPageId(pageId),
    };

    this.logEvent(
      MParticleEvents.CTV_GET_STARTED,
      mParticle.EventType.Other,
      payload,
    );
  }

  reportLoginSkipped(pageId: PageId, loginFlowType: LoginFlow) {
    let formName = '';
    switch (loginFlowType) {
      case LoginFlow.GET_STARTED: {
        formName = 'CTV Get Started';
        break;
      }
      case LoginFlow.SIGN_IN: {
        formName = 'CTV Sign In';
        break;
      }
      case LoginFlow.SIGN_UP: {
        formName = 'CTV Sign Up';
        break;
      }
      case LoginFlow.SIGN_OUT:
        break;
      case LoginFlow.ABSTRACT: {
        console.error(
          '[ReportLoginSkipped] Should not have an Abstract LoginFlow',
        );
        break;
      }
    }

    if (!formName) return;

    const payload: FormSkipPayload = {
      form_name: formName,
      Source_Page: getPageId(pageId),
      Source_Site: getSourceSite(),
    };

    this.logEvent(
      MParticleEvents.FORM_SKIP,
      mParticle.EventType.Other,
      payload,
    );
  }

  reportLogin(props: LoginProps) {
    const { loginType, uid, primaryEmail, pageId } = props;

    const currentUser = mParticle.Identity.getCurrentUser();
    if (currentUser) {
      currentUser.setUserAttributes({ email_address: primaryEmail });
    }

    const payload = {
      login_type: loginType,
      LR_ID: uid,
      Source_Site: getSourceSite(),
      Source_Page: getPageId(pageId),
    } as LoginEventPayload;

    this.logEvent(MParticleEvents.LOGIN, mParticle.EventType.Other, payload);
  }

  reportLogout(props: LogoutProps) {
    const { loginType, uid, pageId } = props;
    const payload = {
      login_type: loginType,
      LR_ID: uid,
      Source_Site: getSourceSite(),
      Source_Page: this.getLogOutSourcePage(pageId),
    } as LogoutEventPayload;

    mParticle.logEvent(
      MParticleEvents.LOGOUT,
      mParticle.EventType.Other,
      payload,
    );
  }

  reportSignUpPassword(
    email: string,
    marketingConsent: boolean,
    pageId: PageId,
  ) {
    const currentUser = mParticle.Identity.getCurrentUser();
    if (currentUser) {
      currentUser.setUserAttributes({
        email_address: email,
        password_set: true,
        marketing_consent: marketingConsent,
      });
    }

    const payload: SignUpPasswordPayload = {
      Source_Site: getSourceSite(),
      Source_Page: getPageId(pageId),
    };

    this.logEvent(
      MParticleEvents.SIGN_UP_PASSWORD,
      mParticle.EventType.Other,
      payload,
    );
  }

  reportSignUpMagicLink(
    email: string,
    marketingConsent: boolean,
    pageId: PageId,
  ) {
    const currentUser = mParticle.Identity.getCurrentUser();
    if (currentUser) {
      currentUser.setUserAttributes({
        email_address: email,
        password_set: false,
        marketing_consent: marketingConsent,
      });
    }

    const payload: SignUpMagicLinkPayload = {
      Source_Site: getSourceSite(),
      Source_Page: getPageId(pageId),
    };

    this.logEvent(
      MParticleEvents.SIGN_UP_MAGIC_LINK,
      mParticle.EventType.Other,
      payload,
    );
  }

  reportChannelEngagement(
    channelName: string,
    option: ChannelEngagementPayload['option_name'],
  ) {
    const payload: ChannelEngagementPayload = {
      channel_name: channelName,
      option_name: option,
    };

    this.logEvent(
      MParticleEvents.CHANNEL_ENGAGEMENT,
      mParticle.EventType.Other,
      payload,
    );
  }

  reportMyStuffAddRemove(props: MyStuffAddRemoveProps) {
    const { type, pageId, rowName } = props;
    const payload: MyStuffAddRemovePayload = {
      Series_Name: type,
      Source_Site: getSourceSite(),
      Source_Page: getPageId(pageId),
      ...(props.actionType === 'Remove' ? { row_name: rowName } : {}),
    };

    const actionType =
      props.actionType === 'Add'
        ? MParticleEvents.MY_STUFF_ADD
        : MParticleEvents.MY_STUFF_REMOVE;

    this.logEvent(actionType, mParticle.EventType.Other, payload);
  }

  reportViewItemList(
    products: ViewItemProps[],
    props: Partial<ItemCustomAttrs>,
    pageId: PageId,
  ) {
    const eCommProducts: Array<mParticle.Product | null> = products.map(
      args => {
        const { data } = args;
        if (!data) return null;

        const props = isContentHub(data)
          ? this.getContentHubProductProps(args)
          : this.getMediaProductProps(args);
        if (!props) return null;

        return createMParticleProduct({
          ...props.productAttributes,
          customProductAttributes: {
            ...props.customAttributes,
            Source_Page: getPageId(pageId),
            Source_Site: getSourceSite(),
          },
        });
      },
    );

    const filteredProducts = eCommProducts.filter(
      p => p !== null,
    ) as mParticle.Product[];

    if (filteredProducts.length === 0) return;

    this.logImpressionEvent('Views', filteredProducts, {
      ...props,
      Source_Site: getSourceSite(),
      Source_Page: getPageId(pageId),
    } as ItemCustomAttrs);
  }

  private getLogOutSourcePage(pageId: PageId) {
    if (pageId === PageId.SETTINGS) {
      return 'Account Settings';
    }
    return getPageId(pageId);
  }

  private getEpisodeName(data: Media | Promo | ContentHub) {
    if (isContentHub(data) || isPromoChannel(data)) {
      return undefined;
    } else {
      return this.getMediaEpisodeName(data as Media);
    }
  }

  private getMediaEpisodeName(media: Media) {
    if (isLiveEvent(media)) {
      return (media as LiveEvent).title;
    } else {
      return isEpisode(media) ? media.title : '';
    }
  }

  private getMediaProductProps = (props: SelectItemProps | ViewItemProps) => {
    const { crossIndex, imageUrl, mainIndex, rowTitle, isTivoTileSource } =
      props;
    const media = props.data as Media | undefined;
    if (!media || mainIndex === undefined || crossIndex === undefined)
      return null;

    const id = media.id ?? media.guid ?? media.slug ?? '';
    const episodeName = this.getEpisodeName(media);
    const seriesName = getSeriesName(media);

    let name = '';
    if (isVideo(media) || isEpisode(media) || isLiveEvent(media)) {
      name = (media as Video | Episode | LiveEvent).title;
    } else {
      name = seriesName;
    }
    name = name ?? '';

    return this.buildProductProps({
      crossIndex,
      imageUrl,
      mainIndex,
      rowTitle,
      id,
      episodeName,
      seriesName,
      name,
      isTivoTileSource,
    });
  };

  private getContentHubProductProps = (
    props: SelectItemProps | ViewItemProps,
  ) => {
    const { crossIndex, imageUrl, mainIndex, rowTitle, isTivoTileSource } =
      props;
    const data = props.data as ContentHub | undefined;
    if (!data || mainIndex === undefined || crossIndex === undefined)
      return null;

    const id = data.id ?? '';
    const seriesName = getSeriesName(data);
    const name = data.title ?? '';

    return this.buildProductProps({
      crossIndex,
      imageUrl,
      mainIndex,
      rowTitle,
      id,
      seriesName,
      name,
      isTivoTileSource,
    });
  };

  private buildProductProps = (props: {
    crossIndex: number;
    imageUrl: string | undefined;
    mainIndex: number;
    rowTitle: string | undefined;
    id: string;
    episodeName?: string | undefined;
    seriesName: string;
    name: string;
    isTivoTileSource: boolean;
  }) => {
    const {
      crossIndex,
      imageUrl,
      rowTitle,
      mainIndex,
      episodeName,
      seriesName,
      isTivoTileSource,
    } = props;
    const customAttributes: ItemCustomAttrs = {
      creative_position: `${crossIndex + 1}`,
      row_name: rowTitle ?? '',
      row_position: `${mainIndex + 1}`,
      Series_Name: seriesName,
      Tile_Source: isTivoTileSource ? 'Tivo Screens' : 'CMS',
      Episode_Name: episodeName ?? '',
    };

    const { id, name } = props;
    return {
      productAttributes: {
        item_id: id,
        item_name: name,
        item_brand: seriesName,
        item_list_name: rowTitle ?? '',
        item_category: mainIndex + 1,
        item_variant: imageUrl ?? '',
      },
      customAttributes,
    };
  };

  updatePrivacyStatus(privacyConsent: PrivacyConsent) {
    this.consentStateOn = privacyConsent;

    const user = mParticle.Identity.getCurrentUser();
    if (!user) return; // User not initialized, can't update yet

    const isConsentOptIn = this.consentStateOn === 'opt-in';

    AppData?.log.debug(
      '[Privacy][MParticle] updatePrivacyStatus',
      `"consent":${privacyConsent}`,
      `"opt-in":${isConsentOptIn}`,
    );

    if (isConsentOptIn) {
      const deviceId = this.deviceIntegration.getDeviceId();
      // Using 'Performance' as Document value to match Android TV
      const ccpaConsentState = mParticle.Consent.createCCPAConsent(
        isConsentOptIn,
        Date.now(), // Timestamp
        'Performance', // Document
        '', // Location
        `IDFA:${deviceId}`, // Hardware ID
      );

      const consentState =
        user.getConsentState() ?? mParticle.Consent.createConsentState();
      consentState.setCCPAConsentState(ccpaConsentState);
      user.setConsentState(consentState);
    } else {
      const consentState = user.getConsentState();
      if (consentState) {
        consentState.removeCCPAConsentState();
        user.setConsentState(consentState);
      }
    }
  }

  private logEvent(
    eventName: string,
    eventType: mParticle.EventType,
    eventInfo: mParticle.SDKEventAttrs,
  ) {
    const payload = { ...eventInfo, Source_Site: getSourceSite() };

    mParticle.logEvent(eventName, eventType, payload);
  }

  private logPromotionEvent(
    promotionType: mParticle.PromotionType,
    promotion: mParticle.Promotion | mParticle.Promotion[],
    attrs?: mParticle.SDKEventAttrs,
  ) {
    const customAttributes = { ...attrs, Source_Site: getSourceSite() };

    mParticle.eCommerce.logPromotion(
      promotionType,
      promotion,
      customAttributes,
    );
  }

  private logImpressionEvent(
    name: string,
    product: mParticle.Product | mParticle.Product[],
    props: ItemCustomAttrs,
  ) {
    const impression = mParticle.eCommerce.createImpression(name, product);
    mParticle.eCommerce.logImpression(impression, props);
  }
}
